import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';

/**
 * OfflinePage
 *
 * A full-screen overlay that is shown when the device has no internet connection.
 * It listens in real-time and automatically disappears once connectivity is restored.
 *
 * Works on: web, iOS (Capacitor), Android (Capacitor).
 */
export const OfflinePage: React.FC = () => {
    const { isOnline } = useNetworkStatus();
    const { t } = useTranslation();
    const [visible, setVisible] = useState(!isOnline);
    const [animating, setAnimating] = useState(false);

    // Animate out when connection is restored
    useEffect(() => {
        if (isOnline && visible) {
            setAnimating(true);
            const timeout = setTimeout(() => {
                setVisible(false);
                setAnimating(false);
            }, 500);
            return () => clearTimeout(timeout);
        }
        if (!isOnline) {
            setVisible(true);
            setAnimating(false);
        }
    }, [isOnline]);

    if (!visible) return null;

    return (
        <div
            className={`fixed inset-0 z-[999999] flex flex-col items-center justify-center bg-slate-950 transition-opacity duration-500 safe-all ${animating ? 'opacity-0' : 'opacity-100'}`}
            role="alert"
            aria-live="assertive"
        >
            {/* Subtle radial gradient background */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(14,165,233,0.07) 0%, transparent 70%)',
                }}
            />

            {/* Animated signal rings */}
            <div className="relative flex items-center justify-center mb-10">
                <span className="absolute w-40 h-40 rounded-full border border-sky-500/10 animate-ping" style={{ animationDuration: '2.4s' }} />
                <span className="absolute w-28 h-28 rounded-full border border-sky-500/15 animate-ping" style={{ animationDuration: '1.8s', animationDelay: '0.3s' }} />
                <span className="absolute w-20 h-20 rounded-full border border-sky-500/20 animate-ping" style={{ animationDuration: '1.2s', animationDelay: '0.6s' }} />

                {/* Icon container */}
                <div className="relative w-20 h-20 rounded-full bg-slate-900 border border-slate-800 shadow-2xl flex items-center justify-center">
                    {/* Wifi-off SVG (inline, no AppIcon dependency needed at this level) */}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-10 h-10 text-sky-400"
                        aria-hidden="true"
                    >
                        <line x1="1" y1="1" x2="23" y2="23" />
                        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
                        <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
                        <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
                        <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
                        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                        <line x1="12" y1="20" x2="12.01" y2="20" />
                    </svg>
                </div>
            </div>

            {/* Text content */}
            <div className="relative text-center px-8 max-w-sm space-y-4">
                <h1 className="text-2xl font-black text-white tracking-tight leading-tight">
                    {t('offline.title', 'Nessuna connessione')}
                </h1>
                <p className="text-slate-400 text-sm leading-relaxed font-medium">
                    {t('offline.description', 'Deskbay richiede una connessione internet per funzionare. Controlla il Wi-Fi o i dati mobili e riprova.')}
                </p>

                {/* Restoring badge */}
                {animating && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-xs font-bold animate-fade-in-pure">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        {t('offline.restoring', 'Connessione ripristinata…')}
                    </div>
                )}
            </div>

            {/* Deskbay wordmark */}
            <div className="absolute bottom-10 safe-bottom flex flex-col items-center gap-1 opacity-30">
                <span className="text-white font-black text-base tracking-widest uppercase">DESKBAY</span>
                <span className="text-slate-500 text-[10px] uppercase tracking-widest font-bold">
                    {t('offline.waiting', 'In attesa di connessione…')}
                </span>
            </div>
        </div>
    );
};
